import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ONBOARDING_STEPS } from './onboardingSteps';
import { useOnboardingStatus } from './useOnboardingStatus';
import { CheckCircle2, Check, ArrowRight, MessageSquare, Bot, Users, Sparkles, X } from 'lucide-react';

const lsGet = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} };
const ssGet = (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) {} };
const ssDel = (k) => { try { sessionStorage.removeItem(k); } catch (e) {} };

const CONFETTI = [
  { left: '12%', color: '#34d399', delay: '0ms', dur: '1.05s' },
  { left: '24%', color: '#A5D62C', delay: '120ms', dur: '1.25s' },
  { left: '38%', color: '#60a5fa', delay: '40ms', dur: '1.1s' },
  { left: '52%', color: '#fbbf24', delay: '200ms', dur: '1.3s' },
  { left: '64%', color: '#34d399', delay: '90ms', dur: '1.15s' },
  { left: '76%', color: '#A5D62C', delay: '160ms', dur: '1.2s' },
  { left: '86%', color: '#60a5fa', delay: '60ms', dur: '1.0s' },
  { left: '46%', color: '#fbbf24', delay: '260ms', dur: '1.35s' },
];

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const on = () => setReduce(mq.matches);
      on();
      mq.addEventListener?.('change', on);
      return () => mq.removeEventListener?.('change', on);
    } catch (e) {}
  }, []);
  return reduce;
}

// Modal guiado de PRIMERA VEZ. Orquesta las pantallas reales (deep-links) sobre el MISMO
// /onboarding/status del checklist; NO reconstruye config. Guardas de continuidad: solo admin,
// nunca bajo suplantacion, all_done nunca auto-abre, first_run=false no molesta, dismissible.
// Compone las primitivas de Radix (no DialogContent) para un overlay con blur, sin tocar el ui/dialog global.
export default function WelcomeOnboardingModal() {
  const { user, impersonating } = useAuth();
  const isAdmin = user?.role === 'admin';
  const cid = user?.company_id || 'tenant';
  const company = (user?.company?.name || user?.company_name || '').trim();
  const navigate = useNavigate();
  const reduce = usePrefersReducedMotion();

  const { raw, steps, applicable, done, total, allDone, firstRun, loading } = useOnboardingStatus(isAdmin);

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('welcome');
  const prevSteps = useRef(null);
  const [justDone, setJustDone] = useState({});

  const SEEN = `dealider_onboarding_seen:${cid}`;
  const RESUME = `dealider_onboarding_resume:${cid}`;

  useEffect(() => {
    if (loading || !raw || !isAdmin || impersonating) return;
    if (ssGet(RESUME) === '1') { ssDel(RESUME); setPhase(allDone ? 'done' : 'steps'); setOpen(true); return; }
    if (lsGet(SEEN) === '1') return;
    if (!firstRun) return;
    setPhase(allDone ? 'done' : 'welcome');
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, raw, isAdmin, impersonating]);

  useEffect(() => {
    const onOpen = () => { setPhase(allDone ? 'done' : 'welcome'); setOpen(true); };
    window.addEventListener('dealider:onboarding:open', onOpen);
    return () => window.removeEventListener('dealider:onboarding:open', onOpen);
  }, [allDone]);

  useEffect(() => {
    if (!steps) return;
    const prev = prevSteps.current;
    if (prev) {
      const changed = {};
      ONBOARDING_STEPS.forEach((s) => { if (prev[s.key] !== 'ok' && steps[s.key] === 'ok') changed[s.key] = true; });
      if (Object.keys(changed).length) {
        setJustDone(changed);
        setTimeout(() => setJustDone({}), 1400);
      }
    }
    prevSteps.current = steps;
  }, [steps]);

  if (!isAdmin) return null;

  const pct = total ? Math.round((done / total) * 100) : 0;
  const dismiss = () => { lsSet(SEEN, '1'); setOpen(false); };
  const goToStep = (to) => { ssSet(RESUME, '1'); setOpen(false); navigate(to); };
  const paneAnim = reduce ? '' : 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-right-2 duration-300';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogPortal>
        <DialogOverlay className="backdrop-blur-sm bg-black/70" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border border-white/10 bg-[#0d1117] shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl overflow-hidden"
          data-testid="welcome-onboarding-modal">
          <DialogTitle className="sr-only">Primeros pasos</DialogTitle>
          <DialogDescription className="sr-only">Guia de configuracion inicial de tu sistema.</DialogDescription>

          {phase === 'welcome' && (
            <div key="welcome" className={paneAnim}>
              <div className="relative h-24 bg-gradient-to-br from-primary/25 via-primary/5 to-transparent">
                <div className="absolute -top-10 -right-8 w-40 h-40 rounded-full bg-primary/20 blur-3xl" aria-hidden="true" />
                <div className="absolute bottom-4 left-6 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/20 text-primary ring-1 ring-primary/30 shadow-lg"><Sparkles className="w-6 h-6" /></div>
              </div>
              <div className="p-6 pt-4">
                <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-primary">Bienvenido{company ? `, ${company}` : ''}</p>
                <h2 className="mt-1 font-heading text-2xl font-bold text-white leading-tight">Deja tu sistema listo para vender</h2>
                <p className="mt-3 text-sm text-muted-foreground">Recibe, contesta y da seguimiento a tus clientes por WhatsApp y telefono, automaticamente, sin que se te escape nadie.</p>
                <p className="mt-2 text-sm text-muted-foreground/90">Son los ajustes esenciales para que empiece a trabajar por ti. Toma unos minutos y solo se hace una vez.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[{ Icon: MessageSquare, t: 'WhatsApp' }, { Icon: Bot, t: 'Asistente IA' }, { Icon: Users, t: 'Tu equipo' }].map(({ Icon, t }) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-[11px] text-white/80 bg-white/5 border border-white/10 rounded-full px-2.5 py-1"><Icon className="w-3 h-3 text-primary" />{t}</span>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                  <Button variant="ghost" onClick={dismiss} className="text-muted-foreground" data-testid="onb-modal-later">Ahora no</Button>
                  <Button autoFocus onClick={() => setPhase('steps')} className="gap-1.5" data-testid="onb-modal-start">Empezar <ArrowRight className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}

          {phase === 'steps' && (
            <div key="steps" className={`p-6 ${paneAnim}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Primeros pasos</h2>
                  <p className="text-xs text-muted-foreground" aria-live="polite">{done} de {total} listos</p>
                </div>
                <span className="text-sm font-bold text-primary tabular-nums">{pct}%</span>
              </div>
              <Progress value={pct} className="mt-3 h-1.5 [&>*]:transition-transform [&>*]:duration-500 [&>*]:ease-out" />
              <div className="mt-4 space-y-2">
                {applicable.map((s, i) => {
                  const st = steps[s.key];
                  const okNow = st === 'ok';
                  const Icon = s.Icon;
                  return (
                    <div key={s.key} style={reduce ? undefined : { animationDelay: `${i * 40}ms`, animationDuration: '320ms' }}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors duration-700 ${justDone[s.key] ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-black/20 border-white/5'} ${reduce ? '' : 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2'}`}
                      data-testid={`onb-modal-step-${s.key}`}>
                      <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ring-1 ring-inset ${okNow ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/20' : 'bg-amber-500/10 text-amber-400 ring-amber-500/15'} ${justDone[s.key] && !reduce ? 'animate-onb-check-pop' : ''}`}>
                        {okNow ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white flex items-center gap-1.5"><span className="text-[10px] text-muted-foreground font-mono">{s.n}.</span>{s.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.why}</p>
                      </div>
                      <Button size="sm" variant={okNow ? 'ghost' : 'default'} onClick={() => goToStep(s.to)} className="shrink-0 gap-1 text-xs" data-testid={`onb-modal-cta-${s.key}`}>
                        {okNow ? 'Revisar' : 'Configurar ahora'} <ArrowRight className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-[11px] text-muted-foreground">Puedes retomar esto cuando quieras desde "Primeros pasos" en tu panel.</p>
                <Button variant="ghost" size="sm" onClick={dismiss} className="text-muted-foreground shrink-0" data-testid="onb-modal-dismiss">Terminar mas tarde</Button>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div key="done" className={`relative p-8 text-center ${paneAnim}`}>
              {!reduce && (
                <div className="pointer-events-none absolute inset-x-0 top-0 h-44 overflow-hidden" aria-hidden="true">
                  {CONFETTI.map((c, i) => (
                    <span key={i} className="absolute top-0 block w-1.5 h-2 rounded-[1px] animate-onb-confetti" style={{ left: c.left, background: c.color, animationDelay: c.delay, animationDuration: c.dur }} />
                  ))}
                </div>
              )}
              <div className="relative mx-auto w-16 h-16">
                {!reduce && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-onb-ring" />}
                <span className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 ${reduce ? '' : 'animate-onb-check-pop'}`}>
                  <CheckCircle2 className="w-8 h-8" />
                </span>
              </div>
              <h2 className="mt-4 font-heading text-2xl font-bold text-white">Tu sistema esta listo</h2>
              <p className="mt-2 text-sm text-muted-foreground">Ya puede recibir y atender clientes automaticamente. Puedes ajustar cualquier cosa cuando quieras.</p>
              <Progress value={100} className="mt-4 h-1.5 max-w-xs mx-auto [&>*]:bg-emerald-500" />
              <div className="mt-6"><Button autoFocus onClick={dismiss} className="gap-1.5" data-testid="onb-modal-finish">Ir a mi panel <ArrowRight className="w-4 h-4" /></Button></div>
            </div>
          )}

          <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10 transition focus:outline-none focus:ring-2 focus:ring-ring" data-testid="onb-modal-close">
            <X className="h-4 w-4" />
            <span className="sr-only">Cerrar</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
